import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Mls10DataSheetRequest, MlsDataSheet as MLSDataSheet} from '../../../../api/ApiTypes.ts';
import SamButton from "../../../SamButton.tsx";
import {formatCurrency, formatDate} from "../../../../utils/dateUtils.ts";
import {getValue} from "../../../../utils/kebabhelper.ts";
import {DetailItem} from "./DetailItem.tsx";
import {CompChangedResponse, SelectableComp, SelectedComp, SlotOption} from "./configurableCompsSelectTypes.tsx";
import {usePropertyValuationApi} from "../../../../context/ApiContext.tsx";
import {NotificationType, useNotification} from "../../../../context/NotificationContext.tsx";
import {compareMlsIdsOrAddressesMatch, getBasementTypeDisplay, isCompTypeActive} from "../../dyanimcFormUtil.tsx";
import {ScanSearch} from "lucide-react";
//because we check in the thing that renders this, we know property valuation is not undefined!
export type SelectableCompWithValuation = SelectableComp & {
    propertyValuation: NonNullable<SelectableComp['propertyValuation']>;
};


interface PropertyDetailsSidebarV2Props {
    clickedSelectableComp: SelectableCompWithValuation;
    options: SlotOption[];
    setClickSelectableComp: (comp: SelectableComp | null) => void;
    selectedComps: SelectedComp[];
    setSelectedCompAtRow: (comp: SelectableComp, slot: SelectedComp) => Promise<CompChangedResponse>;
    handleClickCompImage: (comp: SelectedComp) => void;
    handleClickValuationImages: (images: string[]) => void;
    valuationDate: string | undefined;
    clickedCompIndex?: number; // Optional prop to track clicked comp index
    orderId: string;
}

export const formatNumber = (value: number | null | undefined | string, unit: string = '', fallback: string = 'N/A'): string => {
    if (typeof value === 'string') {
        return `${value}${unit}`;
    }
    if (value === null || value === undefined || isNaN(value)) {
        return fallback;
    }
    return `${new Intl.NumberFormat('en-US').format(value)}${unit}`;
};

const formatBedBath = (
    beds: number | null | undefined,
    baths: number | null | undefined,
    fallback: string = 'N/A'
): string => {
    const bedStr = beds !== null && beds !== undefined ? `${beds} Bed` : null;
    const bathStr = baths !== null && baths !== undefined ? `${baths} Bath` : null;

    if (bedStr && bathStr) {
        return `${bedStr} / ${bathStr}`;
    } else if (bedStr) {
        return bedStr;
    } else if (bathStr) {
        return bathStr;
    } else {
        return fallback;
    }
}



const PropertyDetailsSidebarV2Renderer: React.FC<PropertyDetailsSidebarV2Props> = ({
                                                                                       clickedSelectableComp,
                                                                                       options,
                                                                                       setClickSelectableComp,
                                                                                       selectedComps,
                                                                                       setSelectedCompAtRow,
                                                                                       valuationDate,
                                                                                       handleClickCompImage,
                                                                                       handleClickValuationImages,
                                                                                       clickedCompIndex,
                                                                                       orderId
                                                                                   }) => {


    const [activeFilter, setActiveFilter] = useState<string>("All");
    const [activePanel, setActivePanel] = useState<string>("quickview");
    const [mlsDataSheet, setMlsDataSheet] = useState<MLSDataSheet | null>(null);
    const notification = useNotification();
    const propertyValuationApi = usePropertyValuationApi();
    const [slotOptions] = useState<SlotOption[]>(options);
    const [selectedSlotOption, setSelectedSlotOption] = useState<SlotOption | null>(null);

    const selectedComp = selectedComps.find((selected) =>
        selected.address && clickedSelectableComp.propertyValuation.address
        && compareMlsIdsOrAddressesMatch(selected.address, clickedSelectableComp.propertyValuation.address, selected.mlsId, clickedSelectableComp.propertyValuation.mlsId)
    );

    // Since component only renders when clickedSelectableComp exists, no null checks needed!
    const propertyValuation = clickedSelectableComp.propertyValuation;

    const valuationAllImages = useMemo(() => propertyValuation.allImages ?? [], [propertyValuation]);
    // Pick comparable image first, else valuation image
    const imageSrc = selectedComp
        ? selectedComp.formValues?.[`${selectedComp.dgInfo.prefixForApiPopulation}ImageUrl`]
        : propertyValuation.imageUrl;

    const getPropertyValuation = useCallback(async () => {
        try {
            if (!propertyValuation?.mlsBoardId || !propertyValuation?.mlsId) {
                console.warn("No property valuation data available");
                setMlsDataSheet(null);
                return;
            }

            const request: Mls10DataSheetRequest = {
                orderId: Number(orderId),
                mlsBoardId: propertyValuation.mlsBoardId,
                mlsId: propertyValuation.mlsId,
                valuationDate: valuationDate
            };

            const response = await propertyValuationApi.getMls10DataSheet(request);
            setMlsDataSheet(response?.dataSheet ?? null);
        } catch (error) {
            console.error("Error fetching property valuation:", error);
            notification.showNotification(NotificationType.ERROR, "Failed to load property details");
            setMlsDataSheet(null);
        }
    }, [propertyValuation, valuationDate]);

    useEffect(() => {
        getPropertyValuation();
    }, [getPropertyValuation]);

    const sectionNames = useMemo(() => {
        if (!mlsDataSheet?.rawDescriptions) return ["All"];
        return ["All", ...new Set(mlsDataSheet.rawDescriptions.map(item => item.name))];
    }, [mlsDataSheet]);

    const handleApplyToSlot = async () => {
        if (selectedSlotOption && clickedSelectableComp) {
            try {
                await setSelectedCompAtRow(clickedSelectableComp, selectedSlotOption.selectedComp).then((response) => {
                    if (response.ok) {
                        notification.showNotification(NotificationType.SUCCESS, `Applied to ${selectedSlotOption.label}`);
                    }
                });
            } catch (error) {
                console.error("Error applying to slot:", error);
                notification.showNotification(NotificationType.ERROR, "Failed to apply to slot");
            }
        }
    };

    // Use Lucide icons for close button
    const CloseIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
    );

    const renderSidebarContent = () => {
        const hasDetailedData = !!mlsDataSheet?.rawDescriptions;

        return (
            <>
                {/* Header */}
                <div
                    className="p-4 flex justify-between items-center bg-blue-700 flex-shrink-0 rounded-t-2xl shadow-xs shadow-black">
                    <h2 className="text-lg font-semibold text-white">Property Details</h2>
                    <button
                        onClick={() => setClickSelectableComp(null)}
                        className="text-blue-100 hover:text-white transition-colors p-1 rounded-full hover:bg-blue-600"
                        aria-label="Close property details"
                    >
                        <CloseIcon/>
                    </button>
                </div>

                {/* Top Section (Image, Address, Actions) */}
                <div className="p-4 flex-shrink-0">
                    <div className="flex mb-4">
                        <div className="flex-shrink-0 mr-3">
                            {clickedCompIndex !== undefined && (
                                <div
                                    className={`w-8 h-8 rounded-full ${isCompTypeActive(propertyValuation.compType) ? "bg-green-500" : "bg-blue-600"} text-white flex items-center justify-center text-sm font-semibold shadow-sm`}>
                                    {clickedCompIndex + 1}
                                </div>
                            )}
                        </div>
                        <div className="text-sm font-medium min-w-0">
                            <div className="text-gray-900 font-semibold">{getValue(propertyValuation.address)}</div>
                            <div
                                className="text-gray-600">{getValue(propertyValuation.city)}, {getValue(propertyValuation.state)} {getValue(propertyValuation.zip)}</div>
                        </div>
                    </div>

                    {/* Image */}
                    <div className="mb-4 rounded flex items-center justify-center h-fit overflow-hidden">
                        <div className="relative shrink-0">
                            {imageSrc && ( // if imageSrc is missing/empty, no <img> element shows.
                                <img
                                    src={imageSrc}
                                    alt={propertyValuation.imageUrl ? `Property at ${propertyValuation.address}` : 'Placeholder image'}
                                    className="max-w-fit max-h-48 object-contain rounded-2xl"
                                />
                            )}
                            {selectedComp && (
                                <button
                                    className="absolute bottom-2 right-1 hover:bg-black/20 p-1 rounded-full transition-colors"
                                    onClick={(e) => {
                                        handleClickCompImage(selectedComp);
                                        e.preventDefault();
                                    }}
                                >
                                    <ScanSearch className="h-4 w-4 text-white"/>
                                </button>
                            )}
                            {selectedComp === undefined && valuationAllImages.length > 0 && (
                                <button
                                    className="absolute bottom-2 right-1 hover:bg-black/20 p-1 rounded-full transition-colors"
                                    onClick={(e) => {
                                        handleClickValuationImages(valuationAllImages);
                                        e.preventDefault();
                                    }}
                                >
                                    <ScanSearch className="h-4 w-4 text-white"/>
                                </button>
                            )}
                        </div>

                        {!imageSrc && (
                            <div
                                className="relative w-48 aspect-[4/3] bg-gray-100 rounded overflow-hidden border border-gray-200">
                                <div
                                    className="size-full text-center flex flex-col justify-center items-center border-2 border-dashed border-gray-300 rounded-md">
                                    <p className="text-md text-gray-500">No image</p>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="lucide lucide-image w-full h-1/2 text-gray-300"
                                    >
                                        <rect width="18" height="18" x="3" y="3" rx="2" ry="2"></rect>
                                        <circle cx="9" cy="9" r="2"></circle>
                                        <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"></path>
                                    </svg>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Action Section with Dropdown */}
                    {clickedSelectableComp != undefined && clickedSelectableComp.dgInfo.prefixForApiPopulation != "" && (
                        <div className="mt-2 pt-4 border-t border-gray-200">
                            <div className="flex items-center space-x-2">
                                <label htmlFor="slot-select"
                                       className="text-sm font-medium text-gray-700 flex-shrink-0">
                                    {selectedComp != undefined ? `Applied to: ${selectedComp.dgInfo.requiredType} Slot ${selectedComp.indexOfDg + 1}` : "Apply to:"}
                                </label>

                                {selectedComp === undefined &&
                                    <>
                                        <select
                                            id="slot-select"
                                            value={selectedSlotOption ? slotOptions.indexOf(selectedSlotOption) : ''}
                                            onChange={(e) => {
                                                const index = parseInt(e.target.value);
                                                if (!isNaN(index) && slotOptions[index]) {
                                                    setSelectedSlotOption(slotOptions[index]);
                                                }
                                            }}
                                            className="flex-1 border border-gray-300 rounded-md px-3 py-1 text-sm text-gray-900 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                            disabled={slotOptions.length === 0}
                                        >
                                            {slotOptions.length === 0 ? (
                                                <option value="">No compatible slots available</option>
                                            ) : (
                                                <>
                                                    <option value="" disabled hidden>Choose a comparable slot...</option>

                                                    {slotOptions.map((option, index) => (
                                                        <option key={index} value={index}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </>
                                            )}
                                        </select>

                                        <SamButton
                                            onClick={handleApplyToSlot}
                                            disabled={!selectedSlotOption || slotOptions.length === 0}
                                            size="sm"
                                        >
                                            Apply
                                        </SamButton>
                                    </>
                                }

                            </div>

                            {slotOptions.length === 0 && (
                                <p className="mt-2 text-xs text-gray-500">
                                    No {clickedSelectableComp.dgInfo.requiredType} slots are available for this property
                                    type.
                                </p>
                            )}
                        </div>
                    )}
                </div>


                {/* Tab Button Container */}
                <div className="flex px-4 border-b border-gray-300 flex-shrink-0">
                    <button
                        type="button"
                        onClick={() => setActivePanel("quickview")}
                        aria-current={activePanel === 'quickview' ? 'page' : undefined}
                        className={`
                            px-4 py-2 text-sm font-medium rounded-t-md border-gray-300 border-t border-l border-r
                            focus:outline-none focus:z-10
                            transition-colors duration-150 ease-in-out
                            ${activePanel === 'quickview'
                            ? 'bg-white text-blue-600 border-b-white -mb-px focus:border-t-blue-500 focus:border-l-blue-500 focus:border-r-blue-500'
                            : 'bg-gray-50 text-gray-500 hover:text-gray-700 hover:bg-gray-100 border-b focus:border-blue-500'
                        }
                        `}
                    >
                        Quick View
                    </button>

                    <button
                        type="button"
                        onClick={() => setActivePanel("sourcedata")}
                        aria-current={activePanel === 'sourcedata' ? 'page' : undefined}
                        className={`
                            px-4 py-2 text-sm font-medium rounded-t-md border-gray-300 border-t border-l border-r
                            focus:outline-none focus:z-10
                            transition-colors duration-150 ease-in-out
                            ml-1
                            ${activePanel === 'sourcedata'
                            ? 'bg-white text-blue-600 border-b-white -mb-px focus:border-t-blue-500 focus:border-l-blue-500 focus:border-r-blue-500'
                            : 'bg-gray-50 text-gray-500 hover:text-gray-700 hover:bg-gray-100 border-b focus:border-blue-500'
                        }
                            ${!hasDetailedData ? 'opacity-60 cursor-not-allowed' : ''}
                        `}
                        disabled={!hasDetailedData}
                    >
                        Source Data
                    </button>
                </div>

                {/* Scrollable Content Area */}
                <div
                    className="flex-grow overflow-y-auto bg-white px-4 pt-4 pb-6 border-l border-r border-b border-gray-300 rounded-b-md">
                    {/* Quick View Panel */}
                    <div className={activePanel === 'quickview' ? "grid grid-cols-2 gap-x-5 gap-y-1" : "hidden"}>
                        {/* Column 1 */}
                        <div>
                            <DetailItem label="Sale Date" value={formatDate(propertyValuation.lastSoldDate)}/>
                            <DetailItem label="Sale Price" value={<span
                                className="text-blue-700 font-semibold">{formatCurrency(propertyValuation.lastSoldPrice)}</span>}/>
                            <DetailItem label="Sale Type" value={getValue(propertyValuation.saleType)}/>
                            <DetailItem label="List Date" value={formatDate(propertyValuation.listDate)}/>
                            <DetailItem label="List Price" value={formatCurrency(propertyValuation.listPrice)}/>
                            <DetailItem label="Days on Market"
                                        value={formatNumber(propertyValuation.daysOnMarket, ' Days')}/>
                            <DetailItem label="Proximity"
                                        value={formatNumber(propertyValuation.proximityInMiles, ' mi')}/>
                        </div>
                        {/* Column 2 */}
                        <div>
                            <DetailItem label="Style" value={getValue(propertyValuation.propertyAttributeType)}/>
                            <DetailItem label="Beds / Baths"
                                        value={formatBedBath(propertyValuation.bedroomCount, propertyValuation.fullBathroomCount)}/>
                            <DetailItem label="GLA" value={formatNumber(propertyValuation.grossLivingArea, ' sqft')}/>
                            <DetailItem label="Lot Size" value={formatNumber(propertyValuation.lotSize, ' sqft')}/>
                            <DetailItem label="Basement" value={getBasementTypeDisplay(mlsDataSheet?.basement)}/>
                            <DetailItem label="Year Built" value={getValue(propertyValuation.yearBuilt)}/>
                            <DetailItem label="Price / SqFt" value={formatCurrency(
                                (propertyValuation.listPrice && propertyValuation.grossLivingArea)
                                    ? propertyValuation.listPrice / propertyValuation.grossLivingArea
                                    : null
                            )}/>
                        </div>
                        {/* Comments (Public Remarks) */}
                        <div className="col-span-2 mt-3 pt-3 border-t border-gray-200">
                            <div className="text-sm text-gray-800 font-medium whitespace-pre-line break-words">
                                {propertyValuation?.publicRemarks && propertyValuation?.publicRemarks.toString().trim().length > 0
                                    ? propertyValuation?.publicRemarks
                                    : "No comments available"}
                            </div>
                        </div>
                    </div>

                    {/* Source Data Panel */}
                    <div className={activePanel === 'sourcedata' ? "" : "hidden"}>
                        {!hasDetailedData && (
                            <div className="p-4 text-gray-500">Detailed data not available.</div>
                        )}
                        {hasDetailedData && (
                            <>
                                {/* Filter buttons */}
                                <div className="mb-4">
                                    <div className="flex flex-wrap gap-2">
                                        {sectionNames.map((name) => (
                                            <button
                                                type="button"
                                                key={name}
                                                className={`px-3 py-1 text-xs rounded-full transition-colors ${
                                                    activeFilter === name
                                                        ? "bg-blue-600 text-white"
                                                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                                                }`}
                                                onClick={() => setActiveFilter(name)}
                                            >
                                                {name}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                {/* Data sections */}
                                {(mlsDataSheet?.rawDescriptions || []).map((section, index) => (
                                    <div
                                        key={index}
                                        className={`mb-6 ${activeFilter !== "All" && activeFilter !== section.name ? "hidden" : ""}`}
                                    >
                                        <h3 className="text-base font-semibold text-gray-800 mb-3 border-b border-gray-200 pb-2">
                                            {section.name}
                                        </h3>
                                        <div className="grid grid-cols-2 gap-x-5 gap-y-2">
                                            {section.descriptions.map((item, idx) => (
                                                <React.Fragment key={idx}>
                                                    <div className="text-xs text-gray-500 uppercase tracking-wider">
                                                        {item.displayName}:
                                                    </div>
                                                    <div className="text-sm text-gray-800 font-medium break-words">
                                                        {getValue(item.displayValue)}
                                                    </div>
                                                </React.Fragment>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </>
                        )}
                    </div>
                </div>
            </>
        );
    };

    return (
        <div
            className={`
                fixed top-42 right-5 
                bottom-30 
                w-full sm:w-1/2 md:w-1/3 lg:w-1/4
                bg-white shadow-2xl border-l border-gray-300 shadow-black
                flex flex-col
                transform transition-transform duration-300 ease-in-out
                z-30
                rounded-2xl
                ${propertyValuation != null ? "translate-x-0" : "translate-x-full"}
            `}
        >
            {renderSidebarContent()}
        </div>
    );
}

export const PropertyDetailsSidebarV2 = React.memo(PropertyDetailsSidebarV2Renderer);